import React, { useEffect } from 'react';
import { MatchState } from '../../../types';
import { FEATURE_FLAGS } from '../../../config/featureFlags';
import { useFeedbackSender } from '../../../hooks/useFeedbackSender';
import { FeedbackAnswer, SendStatus } from '../../../hooks/useResultSender';
import { FeedbackForm, FormStatus } from './FeedbackForm';

interface PostMatchFeedbackProps {
  matchState: MatchState;
  /** Called when the user dismisses the form (skip, success auto-close, or external close). */
  onClose: () => void;
}

const AUTO_CLOSE_DELAY_MS = 1500;

const sendStatusToFormStatus = (s: SendStatus): FormStatus => {
  if (s === 'sending') return 'submitting';
  if (s === 'success') return 'success';
  if (s === 'error') return 'error';
  return 'idle';
};

/**
 * Post-match referee feedback questionnaire.
 *
 * Mounting conditions (caller's responsibility):
 *   - match result was successfully sent (so we have a callback URL working)
 *   - feature flag enabled
 *
 * The component itself bails out if the flag is off, so callers can mount it
 * unconditionally without leaking layout if the flag is later removed.
 */
export function PostMatchFeedback({ matchState, onClose }: PostMatchFeedbackProps) {
  const { sendStatus, lastError, sendFeedback, retry } = useFeedbackSender(matchState);

  // Auto-close on success after a brief confirmation delay.
  useEffect(() => {
    if (sendStatus !== 'success') return;
    const timer = setTimeout(onClose, AUTO_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [sendStatus, onClose]);

  if (!FEATURE_FLAGS.postMatchFeedback) {
    return null;
  }

  const handleSubmit = (answers: FeedbackAnswer[]) => {
    sendFeedback(answers);
  };

  return (
    <FeedbackForm
      status={sendStatusToFormStatus(sendStatus)}
      error={lastError}
      onSubmit={handleSubmit}
      onSkip={onClose}
      onRetry={retry}
    />
  );
}
